import { useState, useEffect, useRef } from "react";
import { Upload, X, Image as ImageIcon, Library } from "lucide-react";
import adminApi from "@/lib/adminApi";

/**
 * MediaPicker — modal to pick an existing uploaded media file or upload a new one.
 * Props:
 *   onSelect(url, id) — called with the chosen asset URL and optional id
 *   onClose()
 *   slotLabel — shown in heading
 */
export default function MediaPicker({ onSelect, onClose, slotLabel = "Slot" }) {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    adminApi
      .get("/api/admin/media?limit=100")
      .then((res) => {
        if (!cancelled) {
          const items = Array.isArray(res.data.media)
            ? res.data.media
            : Array.isArray(res.data)
              ? res.data
              : [];
          setMedia(items);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error("[MediaPicker] fetch failed:", err);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", file.name);
      fd.append("category", "template");
      fd.append("group", "Presentation Templates");
      fd.append("status", "published");
      const res = await adminApi.post("/api/admin/media/upload", fd);
      const newItem = res.data;
      setMedia((prev) => [newItem, ...prev]);
      onSelect(newItem.file_url, newItem.id);
    } catch (err) {
      console.error("[MediaPicker] upload failed:", err);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const filtered = media.filter((m) => {
    if (!search) return true;
    return (m.title || m.original_name || "").toLowerCase().includes(search.toLowerCase());
  });

  const isImage = (m) => (m.content_type || "").startsWith("image/");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm" data-testid="media-picker-modal">
      <div className="bg-[#0a0a0a] border border-white/[0.1] w-full max-w-2xl max-h-[80vh] flex flex-col rounded overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-3 p-4 border-b border-white/[0.06] shrink-0">
          <Library size={15} className="text-[#CBA052]" />
          <span className="text-xs font-bold text-white">Pick asset for: <span className="text-[#CBA052]">{slotLabel}</span></span>
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="ml-auto flex items-center gap-1.5 text-xs font-bold text-black bg-[#CBA052] px-3 py-1.5 rounded hover:opacity-90 disabled:opacity-50"
            data-testid="media-picker-upload-btn"
          >
            <Upload size={11} />
            {uploading ? "Uploading..." : "Upload New"}
          </button>
          <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" />
          <button onClick={onClose} className="text-white/30 hover:text-white ml-2" data-testid="media-picker-close-btn"><X size={15} /></button>
        </div>

        {/* Search */}
        <div className="p-3 border-b border-white/[0.04] shrink-0">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by filename..."
            className="w-full bg-white/[0.04] border border-white/10 text-white text-xs rounded px-3 py-2 outline-none"
            data-testid="media-picker-search"
          />
        </div>

        {/* Grid */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <p className="text-xs text-white/30 text-center py-8" data-testid="media-picker-loading">Loading library...</p>
          ) : filtered.length === 0 ? (
            <div className="text-center py-10" data-testid="media-picker-empty">
              <ImageIcon size={24} className="text-white/10 mx-auto mb-3" />
              <p className="text-xs text-white/30">No media found. Upload a new file above.</p>
            </div>
          ) : (
            <div className="grid grid-cols-4 gap-2" data-testid="media-picker-grid">
              {filtered.map((m) => (
                <button
                  key={m.id}
                  onClick={() => onSelect(m.file_url, m.id)}
                  className="group relative aspect-square bg-white/[0.03] rounded overflow-hidden border border-white/[0.06] hover:border-[#CBA052]/50 transition-colors"
                  data-testid={`media-picker-item-${m.id}`}
                  title={m.title || m.original_name}
                >
                  {isImage(m) && (m.thumbnail || m.file_url) ? (
                    <img src={m.thumbnail || m.file_url} alt={m.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ImageIcon size={20} className="text-white/15" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                    <span className="text-[9px] text-white font-bold opacity-0 group-hover:opacity-100 transition-opacity px-1 text-center leading-tight">
                      {(m.title || m.original_name || "").slice(0, 30)}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
